import { TelematicsProviderSource } from '@treadinc/horizon-api-spec';
import { ElementType } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';

import { SystemRoles } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { canViewCompanyOnboarding } from '~hooks/useCompanyOnboarding/companyOnboardingAccess';
import AdminAccountsDataGrid from '~pages/Admin/Accounts/AdminAccountsDataGrid';
import { Admin } from '~pages/Admin/Admin';
import AdminCompaniesDataGrid from '~pages/Admin/Companies/AdminCompaniesDataGrid';
import AdminConnectionsDataGrid from '~pages/Admin/Connections/AdminConnectionsDataGrid';
import ExportRenderers from '~pages/Admin/ExportRenderers/ExportRenderers';
import { AdminGeo } from '~pages/Admin/Geo/AdminGeo';
import { Distances } from '~pages/Admin/Geo/Distances/Distances';
import { AiSummary } from '~pages/Admin/Geo/Jobs/AiSummary';
import { Geofence } from '~pages/Admin/Geo/Jobs/Geofence';
import ManagedCompanies from '~pages/Admin/MangedCompanies/ManagedCompanies';
import MapFiles from '~pages/Admin/MapFiles/MapFiles';
import ReportSettings from '~pages/Admin/ReportSettings/ReportSettings';
import AdminUsersDataGrid from '~pages/Admin/Users/AdminUsersDataGrid';
import { AdminUtilities } from '~pages/Admin/Utilities/AdminUtilities';
import { Approvals } from '~pages/Approvals/Approvals';
import ApprovalsIndexRedirect from '~pages/Approvals/ApprovalsIndexRedirect';
import ApproveCharges from '~pages/Approvals/ApproveCharges/ApproveCharges';
import { DriverDay } from '~pages/Approvals/DriverDay/DriverDay';
import DriverPay from '~pages/Approvals/DriverPayV2/DriverPay';
import ApproveJobsFiles from '~pages/Approvals/Files/Files';
import { TicketsReviewDataGrid } from '~pages/Approvals/TicketsReviewDataGrid/TicketsReviewDataGrid';
import { Timesheets } from '~pages/Approvals/Timesheets/Timesheets';
import Dispatch from '~pages/Dispatch/Dispatch';
import { AccountsDataGrid } from '~pages/Settings/Accounts/AccountsDataGrid';
import { VendorManagement } from '~pages/Settings/Accounts/VendorManagement/VendorManagement';
import { EquipmentManagement } from '~pages/Settings/Administration/EquipmentManagement/EquipmentManagement';
import { MaterialsManagement } from '~pages/Settings/Administration/MaterialsManagement/MaterialsManagement';
import { SitesManagement } from '~pages/Settings/Administration/Sites/SitesManagement';
import { AgentManagement } from '~pages/Settings/Agents/AgentManagement';
import { ApiClientsManagement } from '~pages/Settings/ApiClients/ApiClientsManagement';
import { BillingManagement } from '~pages/Settings/Billing/BillingManagement';
import CompanyOnboardingChecklist from '~pages/Settings/CompanyOnboarding/CompanyOnboardingChecklist';
import { ComplianceDocumentTypesManagement } from '~pages/Settings/ComplianceDocumentTypes/ComplianceDocumentTypesManagement';
import ConfigurationManagement, {
  userHasAccessToConfigurationManagement,
} from '~pages/Settings/Configuration/ConfigurationManagement';
import DeletedRecords from '~pages/Settings/DeletedRecords/DeletedRecords';
import { canViewAnyDeletedRecords } from '~pages/Settings/DeletedRecords/permissions';
import { DepartmentManagement } from '~pages/Settings/DepartmentManagement/DepartmentManagement';
import { EquipmentTypeManagement } from '~pages/Settings/EquipmentTypeManagement/EquipmentTypeManagement';
import { GeneralSettings } from '~pages/Settings/GeneralSettings/GeneralSettings';
import CatenaManagement from '~pages/Settings/Integrations/CatenaManagement';
import CommandAlkonManagement from '~pages/Settings/Integrations/CommandAlkonManagement';
import FastWeighManagement from '~pages/Settings/Integrations/FastWeighManagement';
import { FleetManagement } from '~pages/Settings/Integrations/FleetManagement';
import HaulWareManagement from '~pages/Settings/Integrations/HaulWareManagement';
import { IntegrationManagement } from '~pages/Settings/Integrations/IntegrationManagement';
import RutterManagement from '~pages/Settings/Integrations/RutterManagement';
import RutterQuickBooks from '~pages/Settings/Integrations/RutterQuickBooks';
import VetaCenterManagement from '~pages/Settings/Integrations/VetaCenterManagement';
import { LabelsManagement } from '~pages/Settings/LabelsManagement/LabelsManagement';
import { MaterialTypeManagement } from '~pages/Settings/MaterialTypeManagement/MaterialTypeManagement';
import { OvertimeRuleManagement } from '~pages/Settings/OvertimeRuleManagement/OvertimeRuleManagement';
import { PaymentTermsManagement } from '~pages/Settings/PaymentTermsManagement/PaymentTermsManagement';
import { PrevailingWageBenefitsManagement } from '~pages/Settings/PrevailingWageBenefitsManagement/PrevailingWageBenefitsManagement';
import AddOnCharges from '~pages/Settings/RatesManagement/AddOnCharges/AddOnCharges';
import AutomaticSettlementAddOns from '~pages/Settings/RatesManagement/AutomaticSettlementAddOns/AutomaticSettlementAddOns';
import CommissionDefaults from '~pages/Settings/RatesManagement/CommissionDefaults/CommissionDefaults';
import FuelSurcharge from '~pages/Settings/RatesManagement/FuelSurcharge/FuelSurcharge';
import MaterialRates from '~pages/Settings/RatesManagement/MaterialRates/MaterialRates';
import { RatesManagement } from '~pages/Settings/RatesManagement/RatesManagement';
import RouteOptimizationManagement from '~pages/Settings/RouteOptimization/RouteOptimizationManagement';
import { ServiceClassManagement } from '~pages/Settings/ServiceClasses/ServiceClassManagement';
import { Settings } from '~pages/Settings/Settings';
import { TaxRateScheduleManagement } from '~pages/Settings/TaxRateScheduleManagement/TaxRateScheduleManagement';
import { UserManagementDataGrid } from '~pages/Settings/UserManagement/UserManagementDataGrid';
import UserProfile from '~pages/Settings/UserProfile/UserProfile';
import { RouteHandle } from '~router/routeHandle';
import { ROUTES } from '~router/routePaths';
import UserStore from '~store/UserStore';

import { ProtectedRoute } from './ProtectedRoute';

export const ADMIN_WITH_NESTED_ROUTES: RouteObject = {
  path: ROUTES.admin.base,
  handle: { titleKey: 'page_headings.administration' } satisfies RouteHandle,
  element: (
    <ProtectedRoute element={Admin} requiredRoles={[SystemRoles.PLATFORM_ADMIN]} />
  ),
  children: [
    {
      index: true,
      element: <Navigate to={ROUTES.admin.companies} replace />,
    },
    {
      path: ROUTES.admin.users,
      element: <ProtectedRoute element={AdminUsersDataGrid} />,
    },
    {
      path: ROUTES.admin.companies,
      element: <ProtectedRoute element={AdminCompaniesDataGrid} />,
    },
    {
      path: ROUTES.admin.connections,
      element: <ProtectedRoute element={AdminConnectionsDataGrid} />,
    },
    {
      path: ROUTES.admin.accounts,
      element: <ProtectedRoute element={AdminAccountsDataGrid} />,
    },
    {
      path: ROUTES.admin.managedCompanies,
      element: <ProtectedRoute element={ManagedCompanies} />,
    },
    {
      path: ROUTES.admin.reportSettings,
      element: <ProtectedRoute element={ReportSettings} />,
    },
    {
      path: ROUTES.admin.exportRenderers,
      element: <ProtectedRoute element={ExportRenderers} />,
    },
    {
      path: ROUTES.admin.mapFiles,
      element: <ProtectedRoute element={MapFiles} />,
    },
    {
      path: ROUTES.admin.geo,
      element: <Outlet />,
      children: [
        {
          index: true,
          element: <Navigate to="jobs" replace />,
        },
        {
          path: 'jobs',
          element: (
            <ProtectedRoute
              element={AdminGeo}
              requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
            />
          ),
        },
        {
          path: 'jobs/:jobId',
          element: (
            <ProtectedRoute
              element={AdminGeo}
              requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
            />
          ),
          children: [
            {
              index: true,
              element: <Navigate to="geofences" replace />,
            },
            {
              path: 'geofences',
              element: <Navigate to="geofence_triggers" replace />,
            },
            {
              path: 'geofences/:bottomTab',
              element: (
                <ProtectedRoute
                  element={Geofence}
                  requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
                />
              ),
            },
            {
              path: 'geofences/:bottomTab/:page',
              element: (
                <ProtectedRoute
                  element={Geofence}
                  requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
                />
              ),
            },
            {
              path: 'distances',
              element: (
                <ProtectedRoute
                  element={Distances}
                  requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
                />
              ),
            },
            {
              path: 'ai_summary',
              element: (
                <ProtectedRoute
                  element={AiSummary}
                  requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
                />
              ),
            },
          ],
        },
        {
          path: 'orders',
          element: (
            <ProtectedRoute
              element={AdminGeo}
              requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
            />
          ),
        },
        {
          path: 'orders/:orderId',
          element: (
            <ProtectedRoute
              element={AdminGeo}
              requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
            />
          ),
          children: [
            {
              index: true,
              element: <Navigate to="distances" replace />,
            },
            {
              path: 'distances',
              element: (
                <ProtectedRoute
                  element={Distances}
                  requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
                />
              ),
            },
          ],
        },
        {
          path: 'loads',
          element: (
            <ProtectedRoute
              element={AdminGeo}
              requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
            />
          ),
        },
        {
          path: 'loads/:loadId',
          element: (
            <ProtectedRoute
              element={AdminGeo}
              requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
            />
          ),
          children: [
            {
              index: true,
              element: <Navigate to="distances" replace />,
            },
            {
              path: 'distances',
              element: (
                <ProtectedRoute
                  element={Distances}
                  requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
                />
              ),
            },
          ],
        },
        {
          path: 'driver_days',
          element: (
            <ProtectedRoute
              element={AdminGeo}
              requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
            />
          ),
        },
        {
          path: 'driver_days/:driverDayId',
          element: (
            <ProtectedRoute
              element={AdminGeo}
              requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
            />
          ),
          children: [
            {
              index: true,
              element: <Navigate to="distances" replace />,
            },
            {
              path: 'distances',
              element: (
                <ProtectedRoute
                  element={Distances}
                  requiredRoles={[SystemRoles.PLATFORM_ADMIN]}
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: ROUTES.admin.utilities,
      element: <ProtectedRoute element={AdminUtilities} />,
    },
  ],
};

export const getApprovalsNestedRoutes = (userStore: UserStore): RouteObject => {
  const { canApproveDriverDays, canApproveJob } = userStore.getPermissions();

  return {
    path: ROUTES.approvals.base,
    handle: { titleKey: 'page_headings.approvals' } satisfies RouteHandle,
    element: <ProtectedRoute element={Approvals} />,
    children: [
      {
        index: true,
        element: (
          <ApprovalsIndexRedirect
            canApproveDriverDays={canApproveDriverDays}
            canApproveJob={canApproveJob}
          />
        ),
      },
      {
        path: ROUTES.approvals.driverDay,
        element: <ProtectedRoute element={DriverDay} canAccess={canApproveDriverDays} />,
      },
      {
        path: ROUTES.approvals.driverPay,
        element: canApproveDriverDays ? (
          <ProtectedRoute element={DriverPay} canAccess />
        ) : (
          <ProtectedRoute
            element={DriverPay}
            canAccess={canApproveJob}
            featureFlag={FeatureFlags.chargeBasedSettlements}
          />
        ),
      },
      {
        path: ROUTES.approvals.ticketsReview,
        element: <ProtectedRoute element={TicketsReviewDataGrid} />,
      },
      {
        path: ROUTES.approvals.approveCharges,
        element: (
          <ProtectedRoute
            element={ApproveCharges}
            canAccess={canApproveJob}
            featureFlag={FeatureFlags.chargeBasedSettlements}
          />
        ),
      },
      {
        path: ROUTES.approvals.approveJobs,
        element: <ProtectedRoute element={ApproveJobsFiles} canAccess={canApproveJob} />,
      },
      {
        path: `${ROUTES.approvals.approveJobsByJobId}/:jobId`,
        element: <ProtectedRoute element={ApproveJobsFiles} canAccess={canApproveJob} />,
      },
      {
        path: ROUTES.approvals.timesheets,
        element: (
          <ProtectedRoute
            element={Timesheets}
            canAccess={canApproveDriverDays}
            featureFlag={FeatureFlags.timesheetTab}
          />
        ),
      },
    ],
  };
};

function LegacyDispatchToNewDispatchRedirect() {
  return <Navigate to={`/${ROUTES.dispatch.base}`} replace />;
}

export const DISPATCH_WITH_NESTED_ROUTES: RouteObject = {
  path: ROUTES.dispatch.base,
  handle: { titleKey: 'page_headings.dispatch' } satisfies RouteHandle,
  children: [
    {
      index: true,
      element: <ProtectedRoute element={Dispatch} />,
    },
    // keep a reference to the legacy Dispatch pages for those users that bookmarked it
    {
      path: ROUTES.dispatch.orders,
      element: <LegacyDispatchToNewDispatchRedirect />,
    },
    {
      path: ROUTES.dispatch.jobs,
      element: <LegacyDispatchToNewDispatchRedirect />,
    },
  ],
};

export const companyOnboardingRouteElement = (
  userStore: UserStore,
  element: ElementType,
) => (
  <ProtectedRoute
    element={element}
    canAccess={canViewCompanyOnboarding(userStore)}
    featureFlag={FeatureFlags.companyOnboarding}
  />
);

export const getSettingsNestedRoutes = (userStore: UserStore): RouteObject => {
  const userAccess = userStore.getAccess();
  const userCanEditRoutingSettings = userStore.getPermissions().canEditRoutingSettings;
  const canViewDeletedRecords = canViewAnyDeletedRecords(userStore.getPermissions());

  return {
    path: ROUTES.settings.base,
    handle: { titleKey: 'page_headings.settings' } satisfies RouteHandle,
    element: <ProtectedRoute element={Settings} />,
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.settings.userProfile} replace />,
      },
      {
        path: ROUTES.settings.userProfile,
        element: <ProtectedRoute element={UserProfile} />,
      },
      {
        path: ROUTES.settings.companyOnboarding,
        element: companyOnboardingRouteElement(userStore, CompanyOnboardingChecklist),
      },
      {
        path: ROUTES.settings.generalSettings,
        element: (
          <ProtectedRoute
            element={GeneralSettings}
            canAccess={userAccess.manageCompanies}
          />
        ),
      },
      {
        path: ROUTES.settings.accounts,
        element: (
          <ProtectedRoute
            element={AccountsDataGrid}
            canAccess={userAccess.manageAccounts}
          />
        ),
      },
      {
        path: ROUTES.settings.customers,
        element: (
          <ProtectedRoute
            element={AccountsDataGrid}
            canAccess={userAccess.manageAccounts}
          />
        ),
      },
      {
        path: ROUTES.settings.vendorManagement,
        element: (
          <ProtectedRoute
            element={VendorManagement}
            canAccess={userAccess.manageAccounts}
          />
        ),
      },
      {
        path: ROUTES.settings.vendors,
        element: (
          <ProtectedRoute
            element={AccountsDataGrid}
            canAccess={userAccess.manageAccounts}
          />
        ),
      },
      {
        path: ROUTES.settings.userManagement,
        element: (
          <ProtectedRoute
            element={() => <UserManagementDataGrid />}
            canAccess={userAccess.manageUser}
          />
        ),
      },
      {
        path: ROUTES.settings.drivers,
        element: (
          <ProtectedRoute
            element={() => <UserManagementDataGrid />}
            canAccess={userAccess.manageUser}
          />
        ),
      },
      {
        path: ROUTES.settings.equipmentTypeManagement,
        element: (
          <ProtectedRoute
            element={EquipmentTypeManagement}
            canAccess={userAccess.manageEquipment}
          />
        ),
      },
      {
        path: ROUTES.settings.equipmentManagement,
        element: (
          <ProtectedRoute
            element={EquipmentManagement}
            canAccess={userAccess.manageEquipment}
          />
        ),
      },
      {
        path: ROUTES.settings.materialTypeManagement,
        element: (
          <ProtectedRoute
            element={MaterialTypeManagement}
            canAccess={userAccess.manageMaterials}
          />
        ),
      },
      {
        path: ROUTES.settings.materialsManagement,
        element: (
          <ProtectedRoute
            element={MaterialsManagement}
            canAccess={userAccess.manageMaterials}
          />
        ),
      },
      {
        path: ROUTES.settings.ratesManagement,
        element: (
          <ProtectedRoute element={RatesManagement} canAccess={userAccess.manageRates} />
        ),
      },
      {
        path: ROUTES.settings.addOnCharges,
        element: (
          <ProtectedRoute element={AddOnCharges} canAccess={userAccess.manageRates} />
        ),
      },
      {
        path: ROUTES.settings.materialRatesManagement,
        element: (
          <ProtectedRoute element={MaterialRates} canAccess={userAccess.manageRates} />
        ),
      },
      {
        path: ROUTES.settings.commissionDefaults,
        element: (
          <ProtectedRoute
            element={CommissionDefaults}
            canAccess={userStore.getPermissions().canViewRate}
            featureFlag={FeatureFlags.payCommissions}
          />
        ),
      },
      {
        path: ROUTES.settings.automaticSettlementAddOnManagement,
        element: (
          <ProtectedRoute
            element={AutomaticSettlementAddOns}
            canAccess={userAccess.manageRates}
          />
        ),
      },
      {
        path: ROUTES.settings.fuelSurchargeManagement,
        element: (
          <ProtectedRoute element={FuelSurcharge} canAccess={userAccess.manageRates} />
        ),
      },
      {
        path: ROUTES.settings.fuelPriceIndexManagement,
        element: (
          <Navigate
            to={`/${ROUTES.settings.base}/${ROUTES.settings.fuelSurchargeManagement}`}
            replace
          />
        ),
      },
      {
        path: ROUTES.settings.fuelSurchargeScheduleManagement,
        element: (
          <Navigate
            to={`/${ROUTES.settings.base}/${ROUTES.settings.fuelSurchargeManagement}`}
            replace
          />
        ),
      },
      {
        path: ROUTES.settings.sitesManagement,
        handle: { titleKey: 'administration.site.title' } satisfies RouteHandle,
        element: (
          <ProtectedRoute element={SitesManagement} canAccess={userAccess.manageSites} />
        ),
      },
      {
        path: ROUTES.settings.departmentsManagement,
        element: (
          <ProtectedRoute
            element={DepartmentManagement}
            canAccess={userAccess.manageDepartments}
          />
        ),
      },
      {
        path: ROUTES.settings.paymentTermsManagement,
        element: (
          <ProtectedRoute
            element={PaymentTermsManagement}
            canAccess={userAccess.manageRates}
          />
        ),
      },
      {
        path: ROUTES.settings.labelsManagement,
        element: (
          <ProtectedRoute
            element={LabelsManagement}
            canAccess={userAccess.manageLabels}
          />
        ),
      },
      {
        path: ROUTES.settings.serviceClassesManagement,
        element: (
          <ProtectedRoute
            element={ServiceClassManagement}
            canAccess={userAccess.manageServiceClasses}
          />
        ),
      },
      {
        path: ROUTES.settings.overtimeRulesManagement,
        element: (
          <ProtectedRoute
            element={OvertimeRuleManagement}
            canAccess={userAccess.manageOvertimeRules}
          />
        ),
      },
      {
        path: ROUTES.settings.prevailingWageBenefitsManagement,
        element: (
          <ProtectedRoute
            element={PrevailingWageBenefitsManagement}
            canAccess={userAccess.managePrevailingWageBenefits}
          />
        ),
      },
      {
        path: ROUTES.settings.taxRateScheduleManagement,
        element: (
          <ProtectedRoute
            element={TaxRateScheduleManagement}
            canAccess={userAccess.manageRates}
          />
        ),
      },
      {
        path: ROUTES.settings.configuration,
        element: (
          <ProtectedRoute
            element={ConfigurationManagement}
            canAccess={userHasAccessToConfigurationManagement(userStore)}
          />
        ),
      },
      {
        path: ROUTES.settings.routeOptimization,
        element: (
          <ProtectedRoute
            element={RouteOptimizationManagement}
            canAccess={userCanEditRoutingSettings}
            featureFlag={FeatureFlags.routeOptimization}
          />
        ),
      },
      {
        path: ROUTES.settings.billing,
        element: (
          <ProtectedRoute
            element={BillingManagement}
            canAccess={userAccess.manageBilling}
          />
        ),
      },
      {
        path: ROUTES.settings.complianceDocumentTypes,
        element: (
          <ProtectedRoute
            element={ComplianceDocumentTypesManagement}
            canAccess={userAccess.manageCompliance}
          />
        ),
      },
      {
        path: ROUTES.settings.deletedRecords,
        element: (
          <ProtectedRoute element={DeletedRecords} canAccess={canViewDeletedRecords} />
        ),
      },
      {
        path: ROUTES.settings.agents,
        element: <ProtectedRoute element={AgentManagement} />,
      },
      {
        path: ROUTES.settings.apiClients,
        element: (
          <ProtectedRoute
            element={ApiClientsManagement}
            canAccess={userAccess.manageM2MClients}
          />
        ),
      },
      {
        path: ROUTES.settings.integrations.base,
        children: [
          {
            index: true,
            element: <ProtectedRoute element={IntegrationManagement} />,
          },

          {
            path: ROUTES.settings.integrations.catena.base,
            children: [
              {
                index: true,
                element: <ProtectedRoute element={CatenaManagement} />,
              },
              {
                path: ROUTES.settings.integrations.catena.vehicles,
                element: (
                  <ProtectedRoute
                    element={FleetManagement}
                    providerSource={TelematicsProviderSource.CATENA}
                  />
                ),
              },
            ],
          },
          {
            path: ROUTES.settings.integrations.commandAlkon,
            element: <ProtectedRoute element={CommandAlkonManagement} />,
          },
          {
            path: ROUTES.settings.integrations.haulWare,
            element: <ProtectedRoute element={HaulWareManagement} />,
          },
          {
            path: ROUTES.settings.integrations.fastWeigh,
            element: <ProtectedRoute element={FastWeighManagement} />,
          },
          {
            path: ROUTES.settings.integrations.vetaCenter,
            element: <ProtectedRoute element={VetaCenterManagement} />,
          },
          {
            path: ROUTES.settings.integrations.rutter.base,
            children: [
              {
                index: true,
                element: <ProtectedRoute element={RutterManagement} />,
              },
              {
                path: ROUTES.settings.integrations.rutter.quickBooks,
                element: <ProtectedRoute element={RutterQuickBooks} />,
              },
            ],
          },
        ],
      },
    ],
  };
};
